import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { toast } from '../utils/feedback';

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  
  const navigate = useNavigate();
  const { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } = useAuth();
  
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'reset_form'
  const [errorMessage, setErrorMessage] = useState('');
  
  // For password reset
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  // Password strength calculation
  const getStrengthScore = () => {
    let score = 0;
    if (newPassword.length > 5) score += 1;
    if (newPassword.length > 8) score += 1;
    if (/[A-Z]/.test(newPassword)) score += 1;
    if (/[0-9]/.test(newPassword)) score += 1;
    if (/[^A-Za-z0-9]/.test(newPassword)) score += 1;
    return score;
  };
  
  const strengthScore = getStrengthScore();
  
  const getStrengthColor = () => {
    if (strengthScore <= 1) return 'var(--danger-color)';
    if (strengthScore <= 3) return 'orange';
    if (strengthScore >= 4) return 'var(--secondary-color)';
    return 'var(--border-color)';
  };

  const getStrengthLabel = () => {
    if (newPassword.length === 0) return '';
    if (strengthScore <= 1) return 'Faible';
    if (strengthScore <= 3) return 'Moyen';
    if (strengthScore >= 4) return 'Fort';
    return '';
  };

  useEffect(() => {
    if (!mode || !oobCode) {
      setStatus('error');
      setErrorMessage("Lien invalide ou expiré.");
      return;
    }

    const processAction = async () => {
      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(oobCode);
          setStatus('success');
        } else if (mode === 'resetPassword') {
          const email = await verifyPasswordResetCode(oobCode);
          setResetEmail(email);
          setStatus('reset_form');
        } else {
          setStatus('error');
          setErrorMessage("Action non reconnue.");
        }
      } catch (err) {
        console.error("Action error:", err);
        setStatus('error');
        if (err.code === 'auth/invalid-action-code') {
          setErrorMessage("Ce lien a déjà été utilisé ou a expiré.");
        } else {
          setErrorMessage("Une erreur est survenue lors de la vérification du lien.");
        }
      }
    };

    processAction();
  }, [mode, oobCode, applyActionCode, verifyPasswordResetCode]);

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    
    setStatus('loading');
    try {
      await confirmPasswordReset(oobCode, newPassword);
      setStatus('success');
    } catch (err) {
      console.error("Password reset error:", err);
      setStatus('error');
      setErrorMessage("Erreur lors de la réinitialisation. Le lien a peut-être expiré.");
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      animation: 'fadeIn 0.5s ease-out',
      backgroundColor: 'var(--bg-color)'
    }}>
      {/* Decorative blobs */}
      <div style={{ position: 'fixed', top: '10%', left: '20%', width: '300px', height: '300px', background: 'var(--primary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '10%', right: '20%', width: '250px', height: '250px', background: 'var(--secondary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />

      <div style={{ 
        width: '100%', maxWidth: '500px', 
        background: 'var(--card, #1c1a27)',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
        borderRadius: '24px',
        padding: '2.5rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
        zIndex: 10
      }}>
        {/* Shimmer line */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, var(--primary-color), transparent)', opacity: 0.5 }} />

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <img loading="lazy" src="/logos/logo-officiel-transparent.png" alt="Logo Masse-terclass" style={{ height: '80px', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.4))' }} />
        </div>

        {status === 'loading' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 0' }}>
            <div className="spinner" style={{ margin: '0 auto 1.5rem auto' }}></div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', textAlign: 'center' }}>Traitement en cours...</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem', textAlign: 'center' }}>Patiente quelques instants.</p>
          </div>
        )}

        {status === 'success' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'fadeIn 0.4s ease-out' }}>
            <CheckCircle size={64} color="#4caf50" style={{ marginBottom: '1rem' }} />
            <h2 style={{ fontSize: '1.8rem', margin: '0 0 1rem 0', textAlign: 'center' }}>
              {mode === 'verifyEmail' ? 'E-mail vérifié !' : 'Mot de passe modifié !'}
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', textAlign: 'center' }}>
              {mode === 'verifyEmail' 
                ? "Ton adresse e-mail a bien été validée ! Tu peux fermer cette page."
                : "Ton nouveau mot de passe a été enregistré avec succès. Tu peux maintenant te connecter."}
            </p>
            {mode !== 'verifyEmail' && (
              <button 
                onClick={() => navigate('/')}
                className="btn btn-primary"
                style={{ width: '100%', padding: '1rem', borderRadius: '12px', fontSize: '1.1rem' }}
              >
                Retour à l'accueil
              </button>
            )}
          </div>
        )}

        {status === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'fadeIn 0.4s ease-out' }}>
            <XCircle size={64} color="#ff6b6b" style={{ marginBottom: '1rem' }} />
            <h2 style={{ fontSize: '1.8rem', margin: '0 0 1rem 0', textAlign: 'center' }}>Oups...</h2>
            <div style={{ 
              width: '100%',
              backgroundColor: 'rgba(255, 107, 107, 0.1)', 
              border: '1px solid rgba(255, 107, 107, 0.2)',
              color: '#ff6b6b', padding: '1rem', borderRadius: '12px', 
              marginBottom: '2rem', textAlign: 'center', fontSize: '0.95rem'
            }}>
              {errorMessage}
            </div>
            <button 
              onClick={() => navigate('/')}
              className="btn btn-outline"
              style={{ width: '100%', padding: '1rem', borderRadius: '12px', fontSize: '1.1rem' }}
            >
              Retour à l'accueil
            </button>
          </div>
        )}

        {status === 'reset_form' && (
          <div style={{ textAlign: 'left', animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '1.6rem', textAlign: 'center', margin: '0 0 0.5rem 0' }}>Nouveau mot de passe</h2>
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Pour <strong>{resetEmail}</strong>
            </p>
            
            <form onSubmit={handleResetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-secondary)' }}>Nouveau mot de passe</label>
                <input 
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  style={{ 
                    backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                    padding: '0.8rem', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '1rem',
                    transition: 'all 0.3s ease', width: '100%'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                />
                
                {newPassword.length > 0 && (
                  <div style={{ marginTop: '0.8rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                      <span>Force du mot de passe</span>
                      <span style={{ color: getStrengthColor(), fontWeight: 'bold' }}>{getStrengthLabel()}</span>
                    </div>
                    <div style={{ height: '4px', background: 'var(--glass-border)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ 
                        height: '100%', 
                        width: `${(strengthScore / 5) * 100}%`, 
                        background: getStrengthColor(),
                        transition: 'all 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-secondary)' }}>Confirmer le mot de passe</label>
                <input 
                  type="password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  style={{ 
                    backgroundColor: 'var(--glass-input-bg)', border: '1px solid var(--glass-border)', 
                    padding: '0.8rem', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '1rem',
                    transition: 'all 0.3s ease', width: '100%'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button 
                  type="button" 
                  onClick={() => navigate('/')}
                  style={{ flex: 1, backgroundColor: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', padding: '0.8rem', borderRadius: '12px', cursor: 'pointer', transition: 'background-color 0.2s' }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--glass-input-bg)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  style={{ 
                    flex: 2, padding: '0.8rem', color: '#fff',
                    borderRadius: '12px', fontSize: '1.1rem', fontWeight: 'bold',
                    background: 'linear-gradient(135deg, var(--primary-color), #8c7fbf)',
                    border: 'none', boxShadow: '0 4px 15px rgba(168, 159, 207, 0.3)',
                    cursor: 'pointer', transition: 'opacity 0.2s'
                  }}
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthAction;
